import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BriefcaseBusiness } from "lucide-react";
import {
  SERVICE_MODULES,
  normalizeAllowedModules,
  FREE_MODULE_KEYS,
  getServiceModuleName,
} from "@/config/serviceModules";
import { getModuleManifestByKey } from "@/modules/manifestRegistry";
import {
  showSubscribeFirstPopup,
  showSubscriptionExpiredPopup,
  showUpgradeRequiredPopup,
} from "@/utils/subscriptionAccess";

// The service list and its access rules, shared by the Services page and the services menu
// so both always show the same services with the same subscribed / locked state.
export default function useServiceModules() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const currentStatus = useSelector((s) => s.subscriptions?.currentStatus);
  const allowedModules = useSelector((s) => s.subscriptions?.allowedModules || []);

  const normalizedAllowedModules = useMemo(() => {
    const normalized = new Set(normalizeAllowedModules(allowedModules));
    if (subscribed) {
      FREE_MODULE_KEYS.forEach((key) => normalized.add(key));
    }
    return normalized;
  }, [allowedModules, subscribed]);

  // Modules render from per-module manifests (icon, names, matchers). Do not add central icon maps here.
  const modules = useMemo(
    () =>
      SERVICE_MODULES.filter((module) => !module.hidden).map((module) => ({
        ...module,
        Icon: getModuleManifestByKey(module.key)?.icon || BriefcaseBusiness,
        freeBadge: Boolean(getModuleManifestByKey(module.key)?.freeBadge),
        label: getServiceModuleName(module, i18n.language, "dashboard", t),
        isAvailable: Boolean(subscribed) && normalizedAllowedModules.has(module.key),
      })),
    [i18n.language, normalizedAllowedModules, subscribed, t]
  );

  const openModule = useCallback(
    (module) => {
      const isExpired = String(currentStatus || "").toLowerCase() === "expired";
      if (!subscribed) {
        if (isExpired) {
          showSubscriptionExpiredPopup({
            t,
            onRenew: () => navigate("/subscribe"),
          });
          return;
        }
        showSubscribeFirstPopup({
          t,
          onSubscribe: () => navigate("/subscribe"),
        });
        return;
      }

      if (!normalizedAllowedModules.has(module.key)) {
        showUpgradeRequiredPopup({
          t,
          onUpgrade: () => navigate("/upgrade"),
        });
        return;
      }

      navigate(module.defaultPath);
    },
    [currentStatus, navigate, normalizedAllowedModules, subscribed, t]
  );

  return { modules, openModule };
}
